import { useParentPosition } from "@/hooks/useParentPosition";
import {
  parseDateString,
  parseDateTimeString,
  parseToUTCDate,
} from "@/lib/utils/parser";
import { boundToDate } from "../../DatePicker/DateRangeDropdown";
import { IconCalendar, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useState, useRef } from "react";
import { DatePicker } from "../../DatePicker/DatePicker";
import { useTranslations } from "next-intl";
import { useEventTimezone } from "@/providers/EventProvider";

type TableCellContentDateTimeProps = {
  onFocusAction?: () => void;
  editable: boolean;
  /** Class for the read-only value; defaults to what the table has always used. */
  readTextClass?: string;
  isFilter?: boolean;
  border?: string;
  value: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  iconSize?: number;
  errorVisibility?: boolean;
  fieldName?: string;
  viewOnly?: boolean;
  disabled?: boolean;
  opacityWhenDisabled?: boolean;
  defaultHour?: number;
  /**
   * When false, this behaves as a date-only field: the value renders as
   * `DD.MM.YYYY` (no time-of-day) and the picker hides its TimePicker. Defaults
   * to `true` (date + time), preserving every existing caller.
   */
  time?: boolean;
  /**
   * When false, the ✕ clear affordance is not rendered. For a MANDATORY date —
   * an event's own start/end — clearing produced a PUT that changed nothing,
   * was reported as a successful save, and left the local baseline with no
   * date at all; the backend cannot clear an event date in the first place.
   * Defaults to `true`, preserving every existing caller.
   */
  clearable?: boolean;
  /**
   * IANA timezone for input + display. When omitted, falls back to the active
   * event's tz via `useEventTimezone()`, which itself falls back to the
   * browser tz outside event-scoped surfaces.
   */
  timeZone?: string;
  /**
   * Optional inclusive day bounds. DATE_TIME always keeps the calendar (time of
   * day matters), so these only disable out-of-range days — no dropdown.
   * Accept ISO-Z strings, `"YYYY-MM-DD"`, or Date.
   */
  minDate?: string | Date;
  maxDate?: string | Date;
  /**
   * Shown (muted) in the editable field while NO value is set — without it an
   * empty field renders only the bare calendar icon. Optional; omitted keeps
   * every existing caller byte-identical (empty text, as before).
   */
  placeholder?: string;
  /**
   * Stable hook on the field's wrapper for e2e (`data-testid`). Optional;
   * omitted keeps every existing caller byte-identical.
   */
  testId?: string;
};

export const TableCellContentDateTime: React.FC<
  TableCellContentDateTimeProps
> = ({
  editable,
  readTextClass,
  isFilter,
  border = "border outline-tertiary",
  value,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  error,
  height = "h-9",
  surface = "surface-primary",
  fontSize = "body-2-regular",
  padding = "px-2 py-2",
  rounded = "rounded-lg",
  iconSize = 16,
  errorVisibility = false,
  fieldName,
  viewOnly,
  disabled = false,
  opacityWhenDisabled = true,
  defaultHour,
  time = true,
  clearable = true,
  timeZone,
  minDate,
  maxDate,
  placeholder,
  testId,
}) => {
  const t = useTranslations("optionsMultiSingleSelect");
  // Defensive: only Date/string bounds are meaningful (see TCCDate).
  const minDateObj = boundToDate(minDate);
  const maxDateObj = boundToDate(maxDate);
  const fallbackTz = useEventTimezone();
  const tz = timeZone ?? fallbackTz;
  const browserTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  // Date-only fields drop the time-of-day; everything else keeps date + clock.
  const formatValue = (d: Date | undefined) =>
    time
      ? parseDateTimeString(d, t("clock"), "de-DE", tz, tz !== browserTz)
      : parseDateString(d, "de-DE", tz);

  // 1. Identify State & Refs
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const { dropdownPosition, setDropdownPosition } = useParentPosition();
  const divRef = useRef<HTMLDivElement>(null);

  // Parse the initial value to a Date object
  const [date, setDate] = useState<Date | undefined>(
    parseToUTCDate(value),
  );

  // Format the date for display in the picker's tz, with a TZ suffix when
  // the rendered tz differs from the user's browser tz (helps an admin in
  // NY know they're reading an event time in Berlin).
  const [formattedDate, setFormattedDate] = useState(formatValue(date));

  // Sync internal state when value prop changes from outside
  useEffect(() => {
    const newDate = parseToUTCDate(value);
    setDate((prev) => {
      if (prev?.getTime() === newDate?.getTime()) return prev;
      return newDate;
    });
  }, [value]);

  // Update the formatted date whenever the date or tz changes
  useEffect(() => {
    setFormattedDate(formatValue(date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, t, tz, browserTz, time]);

  // ---------------------------------------------------------
  // ROBUST AUTO-TRACKING PATTERN IMPLEMENTATION
  // ---------------------------------------------------------

  // 2. Immediate Failsafe Effect
  // Forces calculation immediately when state becomes true to prevent (0,0) flicker
  useEffect(() => {
    if (datePickerOpen && divRef.current) {
      setDropdownPosition(divRef);
    }
  }, [datePickerOpen, setDropdownPosition]);

  // 3. Live Tracking Effect (Scroll & Resize)
  useEffect(() => {
    if (datePickerOpen && divRef.current) {
      const updatePosition = () => {
        if (divRef.current) setDropdownPosition(divRef);
      };

      // Capture phase (true) is required for nested scrolling
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [datePickerOpen, setDropdownPosition]);

  // 4. Centralized "Open" Logic
  const handleMouseClick = () => {
    // Prevent opening when viewOnly or disabled is true
    if (viewOnly || disabled) return;

    // Order of Operations: Calculate position FIRST, then show
    if (divRef.current) {
      setDropdownPosition(divRef);
    }
    setDatePickerOpen(true);

    if (onFocusAction) onFocusAction();
  };

  // ---------------------------------------------------------

  const handleClearDate = (e: React.MouseEvent) => {
    // Prevent clearing when viewOnly or disabled is true
    if (viewOnly || disabled) return;

    e.stopPropagation(); // Prevent opening the date picker
    setDate(undefined); // Clear the date
    setFormattedDate(""); // Clear the displayed date
    onChangeValue(""); // Clear the value in the parent component
  };

  return (
    <>
      {isSelected && isEditing && (editable || isFilter) ? (
        // `flex-1` (not bare `w-full`) so that inside a flex row — e.g. the
        // filter row, where the field-name InputColumn is also flex-1 — the
        // date value SHARES the width instead of grabbing basis:100% and
        // collapsing the sibling field-name input to zero. In a block/table
        // context flex-1 is inert and `w-full` still fills the cell.
        <div
          ref={divRef}
          data-testid={testId}
          className="flex-1 w-full min-w-0"
          // While the calendar is open THIS is the node a host probes from: the
          // picker portals to <body>, and the trigger keeps focus (the calendar
          // moves none), so the marker on the portal root alone was unreachable
          // and a capture-phase host closed the whole popup on Escape.
          data-escape-owner={datePickerOpen ? "true" : undefined}
        >
          <button
            aria-expanded={datePickerOpen}
            onClick={handleMouseClick}
            disabled={viewOnly || disabled}
            className={`truncate overflow-hidden ${fontSize} ${height} flex ${surface} w-full ${rounded} ${padding} flex-row font-secondary items-center justify-between ${border} ${
              viewOnly || (opacityWhenDisabled && disabled)
                ? "opacity-50 cursor-not-allowed"
                : ""
            } outline-quaternary`}
          >
            <div className="flex flex-row items-center">
              <IconCalendar size={iconSize} className="mr-2" />
              {/*
                suppressHydrationWarning: `parseDateTimeString` formats dates
                using the user's locale/timezone. Server renders with the
                Node-process locale (UTC), client with the browser locale,
                producing different strings for the same Date. Without this,
                React throws a hydration mismatch and regenerates the whole
                tree on the client — wiping MetaTableProvider's totalCount
                state and breaking the BulkSelectionBanner's initial render.
              */}
              <p
                suppressHydrationWarning
                className={!formattedDate && placeholder ? "font-tertiary" : ""}
              >
                {formattedDate || placeholder || ""}
              </p>
            </div>
            {clearable && value && !viewOnly && !disabled && (
              <div
                onClick={handleClearDate}
                data-testid="tcc-datetime-clear"
                className="flex items-center hover:surface-secondary pl-1"
              >
                <IconX size={iconSize} className="font-secondary" />
              </div>
            )}
          </button>
          {datePickerOpen && !viewOnly && !disabled && (
            <DatePicker
              border={border}
              initialDate={date}
              defaultHour={defaultHour}
              time={time}
              timeZone={tz}
              minDate={minDateObj}
              maxDate={maxDateObj}
              closeDatePicker={() => {
                setDatePickerOpen(false);
              }}
              position={dropdownPosition}
              onSelectDate={(date) => {
                const isoString = date.toISOString();
                setDate(date);
                onChangeValue(isoString);
                setDatePickerOpen(false);
              }}
            />
          )}
          {error && (
            <p className="mt-1 body-3-regular font-error">
              {error}
            </p>
          )}
        </div>
      ) : (
        <div data-testid={testId}>
          {/* See above: locale-dependent formattedDate may differ server vs client. */}
          <p
            className={readTextClass ?? "body-2-regular font-secondary"}
            suppressHydrationWarning
          >
            {formattedDate}
          </p>
          {error && (
            <p className="mt-1 body-3-regular font-error">
              {error}
            </p>
          )}
        </div>
      )}
    </>
  );
};
